import { graphql, useFragment } from "react-relay";
import { SignedActor_actor$key } from "./__generated__/SignedActor_actor.graphql";
import { ActorAvatar } from "./Avatar";
import { AHText } from "../Text";
import { Box } from "../AHTheme/Box";
import { SignedActorText_actor$key } from "./__generated__/SignedActorText_actor.graphql";
import { AHIcon } from "../AHIcon";
import { SignedActorBadge_actor$key } from "./__generated__/SignedActorBadge_actor.graphql";
import { SignedActorCompactBadge_actor$key } from "./__generated__/SignedActorCompactBadge_actor.graphql";
import { SignedActorCompact_actor$key } from "./__generated__/SignedActorCompact_actor.graphql";
import { OrgIcon } from "../OrgIcon";

type ColorMode = "default" | "inverted";
export function SignedActor(props: {
  signedActor: SignedActor_actor$key;
  colorMode?: ColorMode;
}) {
  const colorMode = props.colorMode ?? "default";
  const actor = useFragment(
    graphql`
      fragment SignedActor_actor on Actor {
        ...Avatar_actor
        ...SignedActorText_actor
        ...SignedActorBadge_actor
      }
    `,
    props.signedActor
  );
  return (
    <Wrapper>
      <ActorAvatar actor={actor} size="small" style="default" />

      <Text colorMode={colorMode}>
        <SignedActorText signedActor={actor} />
      </Text>

      <SignedActorBadge signedActor={actor} />
    </Wrapper>
  );
}

export function SignedActorCompact(props: {
  signedActor: SignedActorCompact_actor$key;
  colorMode?: ColorMode;
}) {
  const colorMode = props.colorMode ?? "default";
  const actor = useFragment(
    graphql`
      fragment SignedActorCompact_actor on Actor {
        ...SignedActorText_actor
        ...SignedActorCompactBadge_actor
      }
    `,
    props.signedActor
  );
  return (
    <Wrapper>
      <CompactText colorMode={colorMode}>
        <SignedActorText signedActor={actor} />
      </CompactText>
      <SignedActorCompactBadge signedActor={actor} />
    </Wrapper>
  );
}
function SignedActorBadge(props: { signedActor: SignedActorBadge_actor$key }) {
  const actor = useFragment(
    graphql`
      fragment SignedActorBadge_actor on Actor {
        ... on UserActor {
          storeUser {
            user {
              orgDescription {
                orgIconUri
              }
            }
          }
          __typename
        }

        ... on ConnectActor {
          employee {
            __typename
          }
          __typename
        }
        ... on GroupActor {
          __typename
          employee {
            __typename
          }
          user {
            __typename
          }
        }
      }
    `,
    props.signedActor
  );
  const iconProps = { size: "xsmall", color: "text-washed" } as const;
  switch (actor.__typename) {
    case "UserActor":
      return actor.storeUser?.user?.orgDescription ? (
        <OrgIcon
          orgIconUri={actor.storeUser.user.orgDescription.orgIconUri}
          {...iconProps}
        />
      ) : (
        <AHIcon type="verified-actor" {...iconProps} />
      );
    case "GroupActor":
      return actor.employee ? (
        <AHIcon type="group-actor" {...iconProps} />
      ) : null;
    case "ConnectActor":
      return actor.employee ? <AHIcon type="connect" {...iconProps} /> : null;
  }
  return null;
}

function SignedActorCompactBadge(props: {
  signedActor: SignedActorCompactBadge_actor$key;
}) {
  const actor = useFragment(
    graphql`
      fragment SignedActorCompactBadge_actor on Actor {
        ... on UserActor {
          storeUser {
            user {
              orgDescription {
                orgIconUri
              }
            }
          }
          __typename
        }
        ... on GroupActor {
          __typename
          employee {
            __typename
          }
          user {
            __typename
          }
        }
        ... on ScreenActor {
          __typename
        }
        ... on ConnectActor {
          __typename
        }
      }
    `,
    props.signedActor
  );
  const iconProps = { size: "xsmall", color: "text-washed" } as const;
  switch (actor.__typename) {
    case "UserActor":
      return actor.storeUser?.user?.orgDescription ? (
        <OrgIcon
          orgIconUri={actor.storeUser.user.orgDescription.orgIconUri}
          {...iconProps}
        />
      ) : (
        <AHIcon type="verified-actor" {...iconProps} />
      );
    case "GroupActor":
      return actor.employee ? (
        <AHIcon type="group-actor" {...iconProps} />
      ) : (
        <AHIcon type="guest-actor" {...iconProps} />
      );
    case "ScreenActor":
      return <AHIcon type="tv" {...iconProps} />;
    case "ConnectActor":
      return <AHIcon type="connect" {...iconProps} />;
    default:
      return null;
  }
  return null;
}

export function SignedActorText(props: {
  signedActor: SignedActorText_actor$key;
}) {
  const actor = useFragment(
    graphql`
      fragment SignedActorText_actor on Actor {
        ... on UserActor {
          __typename
          storeUser {
            employee {
              name
            }
            user {
              name
              email
            }
          }
        }
        ... on ScreenActor {
          __typename
          screen {
            name
          }
        }
        ... on GroupActor {
          __typename
          employee {
            name
          }
          identifyingName
        }
        ... on ConnectActor {
          __typename
          identifyingName
          employee {
            name
          }
        }
      }
    `,
    props.signedActor
  );
  switch (actor.__typename) {
    case "UserActor":
      return resolveName(
        actor.storeUser?.employee?.name,
        actor.storeUser?.user?.name,
        actor.storeUser?.user?.email
      );
    case "ScreenActor":
      return actor.screen?.name;
    case "GroupActor":
      return resolveName(actor.employee?.name, actor.identifyingName);
    case "ConnectActor":
      return actor.identifyingName || "Connect";
  }
  return null;
}

function resolveName(...strings: (string | null | undefined)[]) {
  return (
    strings.find(
      (string) => string !== null && string !== undefined && string !== ""
    ) || ""
  );
}

function Wrapper(props: { children: React.ReactNode }) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      {props.children}
    </Box>
  );
}

function Text(props: { children: React.ReactNode; colorMode: ColorMode }) {
  const color =
    props.colorMode === "default" ? "text-primary" : "text-inverted";
  return (
    <AHText color={color} type="detail" spaceLeft="small" spaceRight="xsmall">
      {props.children}
    </AHText>
  );
}
function CompactText(props: {
  children: React.ReactNode;
  colorMode: ColorMode;
}) {
  const color =
    props.colorMode === "default" ? "text-secondary" : "text-inverted";
  return (
    <AHText color={color} type="detail" spaceRight="xsmall">
      {props.children}
    </AHText>
  );
}
